/**
 * Test pdp-cutoff-v1: the delivery line on a product page names the cut-off and the date.
 * Plan: Swyft-Desk desk/plans/2026-10-09-ab-testing-suite.md.
 *
 * It reads what <item-availability> already worked out (next day or not, the delivery date), so the line can never
 * promise a different day from the one shown today. The cart's own counter is not touched.
 */
(() => {
  const TEST = 'pdp-cutoff-v1';
  const COUNTDOWN_FROM = 4 * 60; // minutes before the cut-off when "Order by 3pm" becomes "Order within 2h 34m"

  const london = (ms) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date(ms))
        .map((p) => [p.type, p.value])
    );
    return Number(parts.hour) * 60 + Number(parts.minute);
  };

  const clock = (h, m) => `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
  const span = (minutes) => (minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`);
  const day = (date) => date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '');

  /** The line for this element now, or null when the product doesn't qualify (not in stock, not soon, clocks disagree). */
  async function line(el) {
    const copy = window.SwyftTest.copy;
    const h = window.store_cutoff_hour;
    const m = Number.isFinite(window.store_cutoff_minute) ? window.store_cutoff_minute : 0;
    if (!Number.isFinite(h) || el.isGiftCard || el.normalizeStock() <= 0) return null;
    const date = el.getDeliveryDate();
    if (!(date instanceof Date)) return null;
    const ahead = (date - new Date()) / 86400000;
    if (ahead > 4) return null;
    const left = h * 60 + m - london(await window.SwyftTest.now());
    const fill = (t, values) => Object.entries(values).reduce((s, [k, v]) => s.replace(`__${k}__`, v), t || '');
    if (el.isShowingNextDay) {
      // The page thinks it's before the cut-off but the server says after: show nothing new rather than a wrong promise.
      if (left <= 0) return null;
      const text = left > COUNTDOWN_FROM
        ? fill(copy.cutoffBy, { TIME: clock(h, m), DATE: day(date) })
        : fill(copy.cutoffWithin, { COUNTDOWN: `<span class="time-count">${span(left)}</span>`, DATE: day(date) });
      return { text, nextDay: true, left, cutoff: `${h}:${String(m).padStart(2, '0')}` };
    }
    return { text: fill(copy.orderNow, { DATE: day(date) }), nextDay: false, left, cutoff: `${h}:${String(m).padStart(2, '0')}` };
  }

  async function render(el, arm, state) {
    const result = await line(el);
    let out = el.querySelector('.swyft-test-line');
    if (!result || arm !== 'B') {
      el.classList.remove('swyft-test-b');
      out?.remove();
    } else {
      if (!out) {
        out = document.createElement('span');
        out.className = 'item-availability__message delivery-message is-visible swyft-test-line';
        el.querySelector('[data-message="delivery"]')?.after(out);
      }
      out.classList.toggle('is-next-day', result.nextDay);
      if (out.innerHTML !== result.text) out.innerHTML = result.text;
      el.classList.add('swyft-test-b');
    }
    window.SwyftTest.release();
    if (result) {
      window.SwyftTest.exposure(TEST, arm, {
        surface: 'pdp',
        product_id: el.closest('[data-product-id]')?.dataset.productId || window.ShopifyAnalytics?.meta?.product?.id || null,
        variant_id: el.dataset.variantId,
        text: result.text.replace(/<[^>]+>/g, ''),
        cutoff: result.cutoff,
        stock: el.normalizeStock(),
      });
    }
    // Past the cut-off the element has to work out the next date itself; it never counts down again in view.
    clearTimeout(state.timer);
    if (result?.nextDay && arm === 'B') {
      state.timer = setTimeout(() => (result.left <= 1 ? el.refresh() : render(el, arm, state)), 30000);
    }
  }

  async function start() {
    if (!window.SwyftTest) return;
    await customElements.whenDefined('item-availability');
    const el = document.querySelector('.product-form__availability item-availability');
    if (!el) return window.SwyftTest.release();
    const arm = await window.SwyftTest.arm(TEST);
    if (!arm) return window.SwyftTest.release();
    const state = { timer: null, pending: false };
    // The element rewrites its own line on every variant change; follow it, ignoring our own writes.
    new MutationObserver((records) => {
      if (records.every((r) => r.target.closest?.('.swyft-test-line') || r.target.parentElement?.closest('.swyft-test-line'))) return;
      if (state.pending) return;
      state.pending = true;
      requestAnimationFrame(() => {
        state.pending = false;
        render(el, arm, state);
      });
    }).observe(el, { childList: true, subtree: true, characterData: true });
    render(el, arm, state);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
